import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { SeguroInterface } from "../../../interfaces";
import { useModalMessage } from "../../../components/ModalMessage/useModalMessage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSave, faXmark } from "@fortawesome/free-solid-svg-icons";
import { getUuidV4, goToFocus, rgx_numberOnly, validarCNPJ } from "../../../utils/utils";
import { addSeguro, updateSeguro } from "../../../features/manifestoSlice";
import { TypeRespSeguro } from "../../../lists";

interface FormSeguroProps {
    show: boolean,
    handleClose: () => void;
    id: number | string
}

const initialForm: SeguroInterface = {
    id: 0,
    cnpj: '',
    cpfcnpj: '',
    nApol: '',
    nAver: [],
    respSeg: 1,
    xSeg: '',
}
const SeguroForm: React.FC<FormSeguroProps> = ({ show, handleClose, id }) => {
    const dispatch = useDispatch();
    const manifesto = useSelector((state: RootState) => state.manifesto.manifesto);
    const [form, setForm] = useState<SeguroInterface>(initialForm);
    const [optional, setOpcional] = useState<boolean>(true)
    const lista = useSelector((state: RootState) => state.manifesto.manifesto.seg);
    const cpfCnpjRef = useRef<HTMLInputElement>(null);
    const nomeSeguradoraRef = useRef<HTMLInputElement>(null);
    const CnpjSeguradoraRef = useRef<HTMLInputElement>(null);
    const [change, setChange] = useState<boolean>(false);
    const { alertModal, ModalMessageEl } = useModalMessage();


    const handleSalveSeguro = () => {
        if (validaPreenchimento()) {

            if (lista.find((item: SeguroInterface) => item.cpfcnpj === form.cpfcnpj &&
                item.nApol === form.nApol && item.id !== form.id)) {
                goToFocus(cpfCnpjRef);
                alertModal('Seguro já adicionado.');

            } else {
                dispatch(updateSeguro(form))
                setChange(false);
                setForm(initialForm);
                handleClose();
            }
        }
    }


    const validaPreenchimento = () => {
        if (form.cpfcnpj.trim() === "") {
            goToFocus(cpfCnpjRef);
            alertModal('Informe o CPF/CNPJ');
            return false;
        }

        if (!validarCNPJ(form.cpfcnpj.trim())) {
            goToFocus(cpfCnpjRef);
            alertModal('CNPJ inválido');
            return false;
        }

        if (form.respSeg.toString() === "2") {
            if (form.xSeg.trim() === "") {
                goToFocus(nomeSeguradoraRef);
                alertModal('Informe o nome da seguradora.');
                return false;
            }

            if (form.cnpj.trim() === "") {
                goToFocus(CnpjSeguradoraRef);
                alertModal('Informe o CNPJ');
                return false;
            }

            if (!validarCNPJ(form.cnpj.trim())) {
                goToFocus(CnpjSeguradoraRef);
                alertModal('CNPJ inválido');
                return false;
            }

        }
        return true;
    }

    const handleAdd = () => {
        if (validaPreenchimento()) {
            if (lista.find((item: SeguroInterface) => item.cpfcnpj === form.cpfcnpj
                && item.nApol === form.nApol)) {
                goToFocus(cpfCnpjRef);
                alertModal('Seguro já adicionado.');

            } else {

                dispatch(addSeguro({
                    ...form,
                    id: getUuidV4()
                }))
                setForm(initialForm);
                handleClose();
            }
        }
    }

    const handleChangeSelect = (e: any) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
            xSeg: value === '1' ? '' : prev.xSeg,
            cnpj: value === '1' ? '' : prev.cnpj,
        }))
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        const transformers: Record<string, (val: string) => string> = {
            cpfcnpj: rgx_numberOnly,
            cnpj: rgx_numberOnly,
        };

        const newValue = transformers[name] ? transformers[name](value) : value;

        setForm(prev => ({ ...prev, [name]: newValue }));
    }


    useEffect(() => {
        if (id !== 0) {
            const seguro = manifesto.seg.find((item: SeguroInterface) => item.id.toString() === id.toString());

            if (seguro) {

                setForm({
                    ...initialForm,
                    id: seguro.id,
                    cpfcnpj: seguro.cpfcnpj,
                    nApol: seguro.nApol ?? '',
                    nAver: seguro.nAver ?? [],
                    respSeg: seguro.respSeg ?? '1',
                    xSeg: seguro.xSeg ?? '',
                    cnpj: seguro.cnpj ?? '',
                })

            }

        }
    }, [id])

    useEffect(() => {


        if (form.respSeg.toString() === "1") {
            setOpcional(true)
        } else {
            setOpcional(false)
        }
    }, [form.respSeg])



    return (<Modal show={show} onHide={handleClose} centered backdrop='static' size="lg"
        fullscreen={'xl-down'} scrollable>
        <Modal.Header closeButton className={`py-2 `}>
            <Modal.Title>
                Seguro
            </Modal.Title>
        </Modal.Header>

        <Modal.Body>

            <Row className="mb-3 g-2">
                <Col md={4}>
                    <Form.Group controlId="respSeg">
                        <Form.Label>Responsável pelo Seguro</Form.Label>
                        <Form.Select name="respSeg" value={form.respSeg} onChange={handleChangeSelect}>
                            {TypeRespSeguro.map(x => (
                                <option key={x.id} value={x.id}>{x.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group controlId="seguro-cpfcnpj">
                        <Form.Label>* CNPJ</Form.Label>
                        <Form.Control name="cpfcnpj" value={form.cpfcnpj} maxLength={14}
                            ref={cpfCnpjRef}

                            onChange={handleChange}
                        />
                    </Form.Group>
                </Col>
                <Col md>
                    <Form.Group controlId="seguro-nApol">
                        <Form.Label>Número da Apólice</Form.Label>
                        <Form.Control name="nApol" value={form.nApol} maxLength={20}

                            onChange={handleChange} />
                    </Form.Group>
                </Col>
            </Row>
            <Row className=" g-2">
                <Col md>
                    <Form.Group controlId="seguro-xSeg">
                        <Form.Label>* Nome Seguradora</Form.Label>
                        <Form.Control name="xSeg" value={form.xSeg} disabled={optional} maxLength={30}

                            ref={nomeSeguradoraRef}
                            onChange={handleChange} />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group controlId="seguro-cnpj">
                        <Form.Label>* CNPJ Seguradora</Form.Label>
                        <Form.Control name="cnpj" maxLength={14} value={form.cnpj} disabled={optional}
                            ref={CnpjSeguradoraRef}

                            onChange={handleChange} />
                    </Form.Group>
                </Col>

            </Row>

        </Modal.Body>
        <Modal.Footer className="py-1">
            <Button variant="danger" className="" onClick={() => {
                setForm(initialForm);
                handleClose();
            }} >
                <FontAwesomeIcon icon={faXmark} /> Cancelar
            </Button>

            {id === 0 ? (<>

                <Button variant="primary" className=" me-2" onClick={handleAdd} title="Adicionar seguro"
                    disabled={change}>
                    <FontAwesomeIcon icon={faPlus} /> Adicionar
                </Button>
            </>) : (<>

                <Button className=" me-2" variant="success" onClick={handleSalveSeguro} title="Salvar alterações"
                >
                    <FontAwesomeIcon icon={faSave} /> Salvar
                </Button>

            </>)}

        </Modal.Footer>
        {ModalMessageEl}
    </Modal>)
}

export default SeguroForm;
